import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './TestimonialCarousel.css';

const testimonials = [
  {
    quote: "So many missed climb opportunities while traveling...now I can find reliable belay partners wherever I am!",
    author: "Sarah M.",
    location: "Denver, CO"
  },
  {
    quote: "Finally, a way to find climbing partners who actually show up and know what they're doing. No more awkward crag days...",
    author: "Rachel K.",
    location: "Joshua Tree, CA"
  },
  {
    quote: "Planning climbing trips used to mean weeks of messaging strangers on forums... now I can find vetted partners quickly and plan more trips.",
    author: "Emma T.",
    location: "Portland, OR"
  },
  {
    quote: "I love the vouch system... I finally feel confident trusting someone to catch me on a first climb together.",
    author: "Jordan L.",
    location: "Salt Lake City, UT"
  },
  {
    quote: "As someone who moved to a new city, this is exactly what I need. Found my regular climbing crew in weeks instead of months.",
    author: "Michelle W.",
    location: "Austin, TX"
  }
];

export default function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume autoplay after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleNavClick = (direction) => {
    if (direction === 'next') {
      nextSlide();
    } else {
      prevSlide();
    }
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  return (
    <section className="testimonial-carousel-section" data-testid="testimonial-carousel">
      <p className="testimonial-header">What Climbers Are Saying</p>
      
      <div className="testimonial-carousel-container">
        {/* Previous Arrow */}
        <button 
          className="carousel-nav-btn carousel-prev"
          onClick={() => handleNavClick('prev')}
          aria-label="Previous testimonial"
          data-testid="testimonial-prev-btn"
        >
          <ChevronLeft size={32} strokeWidth={2.5} />
        </button>

        {/* Quote Display */}
        <div className="testimonial-quote-wrapper">
          <div 
            className="testimonial-quote-content"
            key={currentIndex}
          >
            <p className="testimonial-quote-text" data-testid="testimonial-quote">
              "{testimonials[currentIndex].quote}"
            </p>
            <div className="testimonial-author">
              <span className="author-name">{testimonials[currentIndex].author}</span>
              <span className="author-location">{testimonials[currentIndex].location}</span>
            </div>
          </div>
        </div>

        {/* Next Arrow */}
        <button 
          className="carousel-nav-btn carousel-next"
          onClick={() => handleNavClick('next')}
          aria-label="Next testimonial"
          data-testid="testimonial-next-btn"
        >
          <ChevronRight size={32} strokeWidth={2.5} />
        </button>
      </div>

      {/* Pagination Dots */}
      <div className="testimonial-dots" data-testid="testimonial-dots">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`testimonial-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to testimonial ${index + 1}`}
            data-testid={`testimonial-dot-${index}`}
          />
        ))}
      </div>
    </section>
  );
}
